import React, { useCallback, useEffect, useState } from 'react';
import { ImageSize } from '../../../Helpers/ImageLoader';
import { useGuestThumbnail } from '../hooks/useGuestThumbnail';
import type { PdfPage } from '../../../utils/pdf/pdfjs';
import { GuestComponentLocation, GuestLocationMarker } from '../../../types/guestAccess';
import { usePdfDocument } from '../../../hooks/pdf/usePdfDocument';
import PdfPageCanvas from '../../../Components/pdf/PdfPageCanvas';
import ComponentList, {
    ComponentListItem,
    ComponentLocationImage,
} from '../../WorkOrders/components/shared/ComponentList';

interface ResolvedMarker {
    url: string | null;
    loading: boolean;
    page: PdfPage | null;
    onImgError: () => void;
}

/**
 * Renders nothing. Holds the per-marker hooks the guest page needs: the signed
 * URL of the image's `_scaled_600` variant (the frame is at most 360px wide;
 * falls back to the original when the variant is missing) and, for a drawing with a
 * `sourceStoragePath` (plan §5.3), the vector PDF page via pdf.js — which is
 * loaded only when such a marker is shown. Photos and the satellite image stay
 * a plain <img> (D19). The result flows up to the wrapper by `onResolved`.
 */
const GuestMarkerResolver: React.FC<{
    marker: GuestLocationMarker;
    onGetDownloadUrl: (storagePath: string) => Promise<string>;
    onResolved: (storagePath: string, resolved: ResolvedMarker) => void;
}> = ({ marker, onGetDownloadUrl, onResolved }) => {
    const { url, loading, error, onImgError } = useGuestThumbnail(marker.storagePath, onGetDownloadUrl, ImageSize.SCALED_MEDIUM);
    const { page } = usePdfDocument(marker.sourceStoragePath ?? null, { resolveUrl: onGetDownloadUrl });

    useEffect(() => {
        onResolved(marker.storagePath, { url: error ? null : url, loading, page, onImgError });
    }, [marker.storagePath, url, loading, error, page, onImgError, onResolved]);

    return null;
};

interface GuestComponentLocationsProps {
    /** Resolved location metadata keyed by componentKey. */
    componentLocations: Record<string, GuestComponentLocation>;
    /** Display names keyed by componentKey. */
    componentNames: Record<string, string>;
    /** Linked component keys in order; defaults to the keys of `componentLocations`. */
    componentKeys?: string[];
    onGetDownloadUrl: (storagePath: string) => Promise<string>;
}

/**
 * Read-only "Komponenter" + "Hitta komponenterna" for the guest work-order
 * page: a thin wrapper that resolves signed URLs (and vector drawings) and
 * renders the shared `ComponentList` (no add/remove/tap-through). Markers are placed as a fraction of the
 * reference frame (x/refWidth, y/refHeight) — the same scaling the
 * authenticated app uses. Renders nothing when no linked component has any
 * location data and no component keys.
 */
const GuestComponentLocations: React.FC<GuestComponentLocationsProps> = ({
    componentLocations,
    componentNames,
    componentKeys,
    onGetDownloadUrl,
}) => {
    const [resolved, setResolved] = useState<Record<string, ResolvedMarker>>({});
    const handleResolved = useCallback((storagePath: string, value: ResolvedMarker) => {
        setResolved((prev) => ({ ...prev, [storagePath]: value }));
    }, []);

    const entries = Object.entries(componentLocations).filter(
        ([, loc]) => loc.markers.length > 0 || loc.gpsCoordinates
    );

    const keys = componentKeys ?? entries.map(([componentKey]) => componentKey);
    if (keys.length === 0 && entries.length === 0) return null;

    // One resolver per distinct storage path; the same photo may back several markers.
    const markersByPath = new Map<string, GuestLocationMarker>();
    entries.forEach(([, loc]) => loc.markers.forEach((m) => markersByPath.set(m.storagePath, m)));
    const markers = Array.from(markersByPath.values());

    const toImage = (marker: GuestLocationMarker): ComponentLocationImage => {
        const state = resolved[marker.storagePath];
        const hasValidFrame = marker.refWidth > 0 && marker.refHeight > 0;
        const page = state?.page ?? null;
        return {
            label: marker.label,
            imageUrl: state?.url ?? null,
            loading: state ? state.loading : true,
            onImgError: state?.onImgError,
            x: hasValidFrame ? marker.x / marker.refWidth : NaN,
            y: hasValidFrame ? marker.y / marker.refHeight : NaN,
            aspectRatio: hasValidFrame ? marker.refWidth / marker.refHeight : undefined,
            renderOverlay: page
                ? (frame) => (
                    <PdfPageCanvas
                        page={page}
                        boxWidth={frame.width}
                        boxHeight={frame.height}
                        zoom={1}
                        pan={{ x: 0, y: 0 }}
                        containerWidth={frame.width}
                        containerHeight={frame.height}
                    />
                )
                : undefined,
        };
    };

    const components: ComponentListItem[] = keys.map((componentKey) => {
        const loc = componentLocations[componentKey];
        return {
            key: componentKey,
            name: componentNames[componentKey] || 'Komponent',
            subtitle: loc?.locationLabel,
            locations: loc ? loc.markers.map(toImage) : undefined,
            gps: loc?.gpsCoordinates
                ? { lat: loc.gpsCoordinates.latitude, lng: loc.gpsCoordinates.longitude }
                : undefined,
        };
    });

    return (
        <>
            {markers.map((marker) => (
                <GuestMarkerResolver
                    key={marker.storagePath}
                    marker={marker}
                    onGetDownloadUrl={onGetDownloadUrl}
                    onResolved={handleResolved}
                />
            ))}
            <ComponentList components={components} />
        </>
    );
};

export default GuestComponentLocations;
